import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {CornerDotType, CornerSquareType, DotType, ErrorCorrectionLevel, Gradient as QRCodeStylingGradient, Options as QRCodeStylingOptions} from 'qr-code-styling';
import type {RGBA} from '@Utils/color.util';
import {convertFabricColorToFillObject, rgbaArrayToRgbString, rgbaToHexString, rgbaToRGBAString} from '@Utils/color.util';
import {ElementDataType} from '@Libraries/add-media-library';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {FabricQRCodeImage} from '@PosterWhiteboard/items/fabric-qr-code-image';
import type {BaseItemObject, CopyableItemStyles, InitItemOpts, ItemObject} from './item/item.types';
import {ITEM_TYPE} from './item/item.types';
import type {FillObject} from '@PosterWhiteboard/classes/fill.class';
import {Fill, FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {DeepPartial} from '@/global';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {BorderType} from '@PosterWhiteboard/classes/item-border.class';
import {blobToDataUrl, imageUrlToDataUrl, loadImageAsync} from '@Utils/image.util';
import {getUrlForImageItemScreenTier, ImageItemSource, ScreenTier} from '@Libraries/image-item.library';
import {repoImageThumbURL} from '@Libraries/graphic-item-image-library';

export interface QRCodeItemObject extends BaseItemObject {
  message: string;
  qrType: QRType;
  foregroundFill: FillObject;
  backgroundFill: FillObject;
  margin: number;
  backgroundCornerRadius: number;
  dotStyle: QRCodeDotType;
  eyeFrameStyle: QRCodeEyeFrameType;
  eyeBallStyle: QRCodeEyeBallType;
  logoHashedFilename: string | null;
  logoFileExtension: string | null;
  logoSizeRatio: number;
}

/**
 * Narrows a redux `ItemObject` to a `QRCodeItemObject`, so group hooks can read QR-only keys (e.g.
 * `foregroundFill`, `message`) without an unchecked cast.
 */
export const isQRCodeItemObject = (item: ItemObject): item is QRCodeItemObject => {
  return item.gitype === ITEM_TYPE.QR_CODE;
};

export const enum QRType {
  LINK = 'qr_link',
  EMAIL = 'qr_email',
  PHONE_NUMBER = 'qr_phone_number'
}

export enum QRCodeDotType {
  SQUARE = 'square',
  ROUNDED = 'rounded',
  DOTS = 'dots',
  CLASSY = 'classy',
  CLASSY_ROUNDED = 'classy-rounded',
  EXTRA_ROUNDED = 'extra-rounded',
}

export enum QRCodeEyeFrameType {
  SQUARE = 'square',
  DOT = 'dot',
  EXTRA_ROUNDED = 'extra-rounded',
}

export enum QRCodeEyeBallType {
  SQUARE = 'square',
  DOT = 'dot',
}

export const DEFAULT_QR_MESSAGE = 'https://www.postermywall.com';
export const DEFAULT_QR_MAX_LENGTH = 1400;
export const DEFAULT_QR_LOGO_SIZE_RATIO = 0.4;
export const QR_MARGIN_MAX = 100;
export const QR_BACKGROUND_CORNER_RADIUS_MAX = 100;

const DEFAULT_QR_FOREGROUND_COLOR: RGBA = [0, 0, 0, 1];
const DEFAULT_QR_BACKGROUND_COLOR: RGBA = [255, 255, 255, 1];
const DEFAULT_QR_MARGIN = 30;
/**
 * ~16px corner radius at the default item size ((value/100) * 1024 element px, shown at
 * 256 logical px), so new QR codes start as a softly rounded card.
 */
const DEFAULT_QR_BACKGROUND_CORNER_RADIUS = 12;
const QR_GENERATION_SIZE_PX = 2048;
const QR_MAX_MARGIN_FRACTION = 0.25;
const QR_LINEAR_GRADIENT_ROTATION = Math.PI / 2;
const QR_TRANSPARENT_BACKGROUND_COLOR = 'rgba(0,0,0,0)';
const QR_ERROR_CORRECTION_LEVEL: ErrorCorrectionLevel = 'M';
const QR_ERROR_CORRECTION_LEVEL_WITH_LOGO: ErrorCorrectionLevel = 'H';
/**
 * Always clear the QR modules behind the logo: dots showing through transparent logo areas
 * look broken, and EC level H plus the capped logo size keep the code scannable.
 */
const QR_LOGO_HIDE_BACKGROUND_DOTS = true;

export const QR_DOT_TYPE_MAP: Record<QRCodeDotType, DotType> = {
  [QRCodeDotType.SQUARE]: 'square',
  [QRCodeDotType.ROUNDED]: 'rounded',
  [QRCodeDotType.DOTS]: 'dots',
  [QRCodeDotType.CLASSY]: 'classy',
  [QRCodeDotType.CLASSY_ROUNDED]: 'classy-rounded',
  [QRCodeDotType.EXTRA_ROUNDED]: 'extra-rounded',
};

export const QR_EYE_FRAME_TYPE_MAP: Record<QRCodeEyeFrameType, CornerSquareType> = {
  [QRCodeEyeFrameType.SQUARE]: 'square',
  [QRCodeEyeFrameType.DOT]: 'dot',
  [QRCodeEyeFrameType.EXTRA_ROUNDED]: 'extra-rounded',
};

export const QR_EYE_BALL_TYPE_MAP: Record<QRCodeEyeBallType, CornerDotType> = {
  [QRCodeEyeBallType.SQUARE]: 'square',
  [QRCodeEyeBallType.DOT]: 'dot',
};

const createFill = (fillType: FillTypes, fillColor: RGBA[]): Fill => {
  const fill = new Fill();
  fill.fillType = fillType;
  fill.fillColor = fillColor;
  return fill;
};

interface QRCodeStylingColorConfig {
  color?: string;
  gradient?: QRCodeStylingGradient;
}

const getQRCodeStylingFillColorConfig = (fill: Fill): QRCodeStylingColorConfig => {
  if (fill.hasGradientFill()) {
    return {
      gradient: {
        type: fill.hasLinearGradientFill() ? 'linear' : 'radial',
        rotation: fill.hasLinearGradientFill() ? QR_LINEAR_GRADIENT_ROTATION : 0,
        colorStops: fill.fillColor.map((color, index) => ({
          offset: fill.fillColor.length > 1 ? index / (fill.fillColor.length - 1) : 0,
          color: rgbaToRGBAString(color),
        })),
      },
    };
  }
  return {color: rgbaToRGBAString(fill.fillColor[0])};
};

const getQRCodeStylingBackgroundColorConfig = (fill: Fill): QRCodeStylingColorConfig => {
  if (!fill.hasFill()) {
    return {color: QR_TRANSPARENT_BACKGROUND_COLOR};
  }
  return getQRCodeStylingFillColorConfig(fill);
};

export class QRCodeItem extends Item {
  public message = DEFAULT_QR_MESSAGE;
  public qrType: QRType = QRType.LINK;
  public foregroundFill: Fill = createFill(FillTypes.SOLID, [DEFAULT_QR_FOREGROUND_COLOR]);
  public backgroundFill: Fill = createFill(FillTypes.SOLID, [DEFAULT_QR_BACKGROUND_COLOR]);
  public margin = DEFAULT_QR_MARGIN;
  public backgroundCornerRadius = DEFAULT_QR_BACKGROUND_CORNER_RADIUS;
  public dotStyle: QRCodeDotType = QRCodeDotType.SQUARE;
  public eyeFrameStyle: QRCodeEyeFrameType = QRCodeEyeFrameType.SQUARE;
  public eyeBallStyle: QRCodeEyeBallType = QRCodeEyeBallType.SQUARE;
  public logoHashedFilename: string | null = null;
  public logoFileExtension: string | null = null;
  public logoSizeRatio = DEFAULT_QR_LOGO_SIZE_RATIO;
  public gitype: ITEM_TYPE.QR_CODE = ITEM_TYPE.QR_CODE;

  public override copyVals(obj: DeepPartial<QRCodeItemObject>): void {
    const {foregroundFill, backgroundFill, ...plainObj} = obj;
    super.copyVals(plainObj);
    this.foregroundFill.copyVals(foregroundFill);
    this.backgroundFill.copyVals(backgroundFill);
  }

  public override async updateFromObject(obj: DeepPartial<QRCodeItemObject>, opts: UpdateFromObjectOpts = {}): Promise<void> {
    await super.updateFromObject(this.#withRoundnessResetWhenContainerRemoved(obj), opts);
  }

  /**
   * When an update turns off the last visible container edge (background and outline both
   * off), the hidden roundness resets in the same undoable operation, so the roundness
   * control starts from zero whenever it reappears. Updates that set the radius themselves
   * (full-snapshot loads, undo/redo diffs) pass through untouched.
   */
  #withRoundnessResetWhenContainerRemoved(obj: DeepPartial<QRCodeItemObject>): DeepPartial<QRCodeItemObject> {
    if (obj.backgroundCornerRadius !== undefined || this.backgroundCornerRadius === 0) {
      return obj;
    }

    const updatedFillType = obj.backgroundFill?.fillType ?? this.backgroundFill.fillType;
    const updatedBorderType = obj.border?.solidBorderType ?? this.border.solidBorderType;
    if (updatedFillType === FillTypes.NONE && updatedBorderType === BorderType.NONE) {
      return {...obj, backgroundCornerRadius: 0};
    }
    return obj;
  }

  protected override itemObjectHasDestructiveChanges(oldItemObject: QRCodeItemObject): boolean {
    return (
      oldItemObject.message !== this.message ||
      JSON.stringify(oldItemObject.foregroundFill) !== JSON.stringify(this.foregroundFill.toObject()) ||
      JSON.stringify(oldItemObject.backgroundFill) !== JSON.stringify(this.backgroundFill.toObject()) ||
      oldItemObject.margin !== this.margin ||
      oldItemObject.backgroundCornerRadius !== this.backgroundCornerRadius ||
      oldItemObject.dotStyle !== this.dotStyle ||
      oldItemObject.eyeFrameStyle !== this.eyeFrameStyle ||
      oldItemObject.eyeBallStyle !== this.eyeBallStyle ||
      oldItemObject.logoHashedFilename !== this.logoHashedFilename ||
      oldItemObject.logoFileExtension !== this.logoFileExtension ||
      oldItemObject.logoSizeRatio !== this.logoSizeRatio
    );
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      frontFill: rgbaArrayToRgbString(this.foregroundFill.fillColor[0]),
      backFill: this.backgroundFill.toObject(),
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<QRCodeItemObject> = {};
    if (copiedStyles.frontFill !== undefined) {
      stylesObj.foregroundFill = {
        fillType: FillTypes.SOLID,
        fillColor: [convertFabricColorToFillObject(copiedStyles.frontFill).fillColor[0]],
      };
    }

    if (copiedStyles.backFill !== undefined) {
      stylesObj.backgroundFill = copiedStyles.backFill;
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['shadow', 'border', 'flipX', 'flipY', 'scaleX', 'scaleY']);
    await this.updateFromObject({...commonStylesObj, ...stylesObj});
  }

  public override isPDFSafe(): Promise<boolean> {
    // The container border is drawn in FabricQRCodeImage._render only; rasterizing for
    // PDF/SVG generation captures it, a vector export would not.
    return Promise.resolve(false);
  }

  public override hasClickableLink(): boolean {
    return false;
  }

  public override toObject(): QRCodeItemObject {
    return {
      ...super.toObject(),
      message: this.message,
      qrType: this.qrType,
      foregroundFill: this.foregroundFill.toObject(),
      backgroundFill: this.backgroundFill.toObject(),
      margin: this.margin,
      backgroundCornerRadius: this.backgroundCornerRadius,
      dotStyle: this.dotStyle,
      eyeFrameStyle: this.eyeFrameStyle,
      eyeBallStyle: this.eyeBallStyle,
      logoHashedFilename: this.logoHashedFilename,
      logoFileExtension: this.logoFileExtension,
      logoSizeRatio: this.logoSizeRatio,
    };
  }

  public hasLogo(): boolean {
    return this.logoHashedFilename !== null && this.logoFileExtension !== null;
  }

  public hasTransparentBackground(): boolean {
    return !this.backgroundFill.hasFill();
  }

  public toggleBackgroundStateForQRItem = (): void => {
    void this.updateFromObject({
      backgroundFill: {
        fillType: this.backgroundFill.hasFill() ? FillTypes.NONE : FillTypes.SOLID,
      },
    });
  };

  public updateLogo = (hashedFilename: string, fileExtension: string): void => {
    void this.updateFromObject({
      logoHashedFilename: hashedFilename,
      logoFileExtension: fileExtension,
    });
  };

  public removeLogo = (): void => {
    void this.updateFromObject({
      logoHashedFilename: null,
      logoFileExtension: null,
    });
  };

  protected async getFabricObjectForItem(opts: InitItemOpts = {}): Promise<FabricQRCodeImage> {
    const imageElement = await this.#generateQRCodeImage();
    return new FabricQRCodeImage(imageElement, {
      ...this.getCommonOptions(),
      scaleX: opts?.scaleX ?? 1,
      scaleY: opts?.scaleY ?? 1,
      ...this.getScalesAndShadowToMaintainScaledDimensions(imageElement.width, imageElement.height, opts),
      ...this.#getContainerBorderProps(),
    });
  }

  /**
   * The generic rect stroke cannot follow the container's corner radius, so the border is
   * drawn by FabricQRCodeImage instead of the base class's fabric stroke.
   */
  protected override applyBorder(): void {
    this.fabricObject.set(this.#getContainerBorderProps());
  }

  #getContainerBorderProps(): {qrBorderColor: string; qrBorderThickness: number; qrBorderRadius: number} {
    return {
      qrBorderColor: rgbaToHexString(this.border.solidBorderColor),
      qrBorderThickness: this.border.hasBorder() ? this.border.solidBorderThickness : 0,
      qrBorderRadius: (this.backgroundCornerRadius / QR_BACKGROUND_CORNER_RADIUS_MAX) * (QR_GENERATION_SIZE_PX / 2),
    };
  }

  protected override onItemDoubleTapped(): void {
    getPosterUi().openPosterEditorQRContentModal();
  }

  async #generateQRCodeImage(): Promise<HTMLImageElement> {
    const qrCodeStylingModule = await import('qr-code-styling');
    const qrCodeStyling = new qrCodeStylingModule.default(await this.#getQRCodeStylingOptions());
    const blob = await qrCodeStyling.getRawData('png');
    if (!(blob instanceof Blob)) {
      throw new Error(`Failed to generate QR code image for message ${this.message}`);
    }
    return loadImageAsync(await blobToDataUrl(blob));
  }

  async #getQRCodeStylingOptions(): Promise<QRCodeStylingOptions> {
    const foregroundColorConfig = getQRCodeStylingFillColorConfig(this.foregroundFill);
    const options: QRCodeStylingOptions = {
      width: QR_GENERATION_SIZE_PX,
      height: QR_GENERATION_SIZE_PX,
      margin: this.#getGenerationMarginPx(),
      data: this.message,
      qrOptions: {
        errorCorrectionLevel: this.hasLogo() ? QR_ERROR_CORRECTION_LEVEL_WITH_LOGO : QR_ERROR_CORRECTION_LEVEL,
      },
      dotsOptions: {
        type: QR_DOT_TYPE_MAP[this.dotStyle],
        ...foregroundColorConfig,
      },
      cornersSquareOptions: {
        type: QR_EYE_FRAME_TYPE_MAP[this.eyeFrameStyle],
        ...foregroundColorConfig,
      },
      cornersDotOptions: {
        type: QR_EYE_BALL_TYPE_MAP[this.eyeBallStyle],
        ...foregroundColorConfig,
      },
      backgroundOptions: {
        round: this.backgroundCornerRadius / QR_BACKGROUND_CORNER_RADIUS_MAX,
        ...getQRCodeStylingBackgroundColorConfig(this.backgroundFill),
      },
    };

    if (this.hasLogo()) {
      options.image = await imageUrlToDataUrl(this.#getLogoUrl());
      options.imageOptions = {
        imageSize: this.logoSizeRatio,
        hideBackgroundDots: QR_LOGO_HIDE_BACKGROUND_DOTS,
        margin: 0,
      };
    }

    return options;
  }

  #getGenerationMarginPx(): number {
    return (this.margin / QR_MARGIN_MAX) * QR_GENERATION_SIZE_PX * QR_MAX_MARGIN_FRACTION;
  }

  #getLogoUrl(): string {
    if (this.page.poster.isHighRes) {
      return getUrlForImageItemScreenTier(this.logoHashedFilename ?? '', ImageItemSource.UPLOAD, ScreenTier.SMALL);
    }
    return repoImageThumbURL(this.logoHashedFilename ?? '', this.logoFileExtension ?? '');
  }
}

export const addQrCodeToPage = (message?: string, top?: number, left?: number): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  const opts = {message: message  ?? window.posterEditor?.config?.businessProfileUrl ?? DEFAULT_QR_MESSAGE};
  void currentPage.items.addItems.addQRItem(
    {
      type: ElementDataType.QR_CODE,
      ...(top !== undefined && {top}),
      ...(left !== undefined && {left}),
    },
    opts
  );
};
